import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import { SidebarProvider, useSidebar } from '@/components/ui/sidebar'
import type { HelmAccess } from '@/src/lib/roles'
import { readProgress, writeProgress } from '@/src/lib/onboarding/progress'
import {
  reduceOnboarding,
  shouldOfferChecklist,
  startIndex,
  toursFor,
  type OnboardingEvent,
  type OnboardingState,
  type TourDefinition,
  type TourProgress,
} from '@/src/lib/onboarding/tour-machine'
import { useProgressSync } from './useProgressSync'
import { TOURS } from '@/src/lib/onboarding/tours'
import { GettingStarted } from './GettingStarted'
import { TourLayer } from './TourLayer'

interface OnboardingContextValue {
  /** The tours this person can take. */
  tours: TourDefinition[]
  progress: TourProgress
  activeTourId: string | null
  /** The `data-tour` name the running step points at, so the menu can show it. */
  activeTarget: string | null
  openChecklist: () => void
  /** From the beginning (or where the page they're on comes in), or where they left off. */
  start: (tourId: string, options?: { resume?: boolean }) => void
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null)

export function useOnboarding() {
  const context = useContext(OnboardingContext)
  if (!context) throw new Error('useOnboarding must be used within OnboardingProvider.')
  return context
}

const now = () => new Date().toISOString()

/**
 * Getting started and the guided tours, for one signed-in person. Offered by
 * itself once, on someone's first visit home; reopened from Help. Key it
 * by the user, so a different login starts from its own progress.
 */
export function OnboardingProvider({
  userId,
  access,
  children,
}: {
  userId: string
  /** Null until the profile is known: nothing is offered before then. */
  access: HelmAccess | null
  children: ReactNode
}) {
  const location = useLocation()
  const sidebar = useSidebar()
  const tours = useMemo(() => toursFor(TOURS, access), [access])
  const [state, setState] = useState<OnboardingState>(() => ({
    active: null,
    progress: readProgress(userId),
  }))
  const [checklistOpen, setChecklistOpen] = useState(false)
  const [focusOnStart, setFocusOnStart] = useState(0)

  const dispatch = useCallback(
    (event: OnboardingEvent) => setState((current) => reduceOnboarding(current, event, tours)),
    [tours],
  )

  useEffect(() => {
    writeProgress(userId, state.progress)
  }, [userId, state.progress])

  // Kept on the server too, so it follows the person to their other devices.
  const setProgress = useCallback(
    (update: (current: TourProgress) => TourProgress) =>
      setState((current) => ({ ...current, progress: update(current.progress) })),
    [],
  )
  const { ready } = useProgressSync(userId, state.progress, setProgress)

  const offer =
    ready && !state.active && shouldOfferChecklist(state.progress, access, tours, location.pathname)
  useEffect(() => {
    if (!offer) return
    setChecklistOpen(true)
    dispatch({ type: 'OFFERED', at: now() })
  }, [offer, dispatch])

  const start = useCallback(
    (tourId: string, options: { resume?: boolean } = {}) => {
      const tour = tours.find((candidate) => candidate.id === tourId)
      if (!tour) return
      const lastStep = state.progress.tours[tourId]?.lastStep
      const index =
        options.resume && lastStep !== undefined ? lastStep : startIndex(tour, location.pathname)
      setChecklistOpen(false)
      setFocusOnStart((count) => count + 1)
      dispatch({ type: 'START', tourId, index, at: now() })
    },
    [tours, state.progress, location.pathname, dispatch],
  )

  const active = state.active
  const tour = active ? tours.find((candidate) => candidate.id === active.tourId) : undefined
  const step = active && tour ? tour.steps[active.index] : undefined
  const activeTarget = step?.target ?? null

  const value = useMemo<OnboardingContextValue>(
    () => ({
      tours,
      progress: state.progress,
      activeTourId: state.active?.tourId ?? null,
      activeTarget,
      openChecklist: () => setChecklistOpen(true),
      start,
    }),
    [tours, state.progress, state.active?.tourId, activeTarget, start],
  )

  // On a phone the menu is a drawer: open it for a step about a menu item, and
  // close it again once the tour moves on to the page, or ends. Only when the
  // step changes, so someone opening or closing it themselves isn't overruled.
  const stepKey = active && tour ? `${tour.id}:${active.index}` : null
  const menuStep = step?.fallback?.target === 'nav-menu'
  const sidebarRef = useRef(sidebar)
  const openedMenu = useRef(false)
  useEffect(() => {
    sidebarRef.current = sidebar
  })
  useEffect(() => {
    const { isMobile, openMobile, setOpenMobile } = sidebarRef.current
    if (!isMobile) return
    if (stepKey && menuStep) {
      if (!openMobile) {
        setOpenMobile(true)
        openedMenu.current = true
      }
      return
    }
    if (openMobile && (stepKey || openedMenu.current)) setOpenMobile(false)
    openedMenu.current = false
  }, [stepKey, menuStep])

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      <GettingStarted open={checklistOpen} onOpenChange={setChecklistOpen} />
      {active && tour && step && (
        <TourLayer
          key={tour.id}
          tour={tour}
          index={active.index}
          dispatch={dispatch}
          focusRequest={focusOnStart}
        />
      )}
    </OnboardingContext.Provider>
  )
}

/**
 * The sidebar with Getting started and the tours inside it: the tours open
 * and close the menu drawer on a phone.
 */
export function SidebarWithOnboarding({
  userId,
  access,
  children,
}: {
  userId: string
  access: HelmAccess | null
  children: ReactNode
}) {
  return (
    <SidebarProvider>
      <OnboardingProvider key={userId} userId={userId} access={access}>
        {children}
      </OnboardingProvider>
    </SidebarProvider>
  )
}
